import React, { useState, useEffect } from 'react';
import { X, Check, Loader2, ArrowRight } from 'lucide-react';

function QuoteModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        projectType: 'Web Development',
        budget: '50k - 100k KES',
        details: ''
    });
    const [status, setStatus] = useState('idle');

    useEffect(() => {
        if (isOpen) document.body.classList.add('menu-open');
        else document.body.classList.remove('menu-open');
        return () => document.body.classList.remove('menu-open');
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        
        setTimeout(() => {
            setStatus('success');
            setTimeout(() => {
                onClose();
                setStatus('idle');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    projectType: 'Web Development',
                    budget: '50k - 100k KES',
                    details: ''
                });
            }, 2000);
        }, 1500);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm modal-overlay" onClick={onClose}></div>
            
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative z-10 overflow-hidden modal-content flex flex-col max-h-[90vh]">
                <div className="bg-slate-900 p-4 md:p-6 flex justify-between items-center text-white shrink-0">
                    <div>
                        <h3 className="text-lg md:text-xl font-bold">Request a Quote</h3>
                        <p className="text-slate-400 text-xs md:text-sm">Kickstart your project today.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>
                
                <div className="p-4 md:p-6 overflow-y-auto">
                    {status === 'success' ? (
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check size={32} />
                            </div>
                            <h4 className="text-xl font-bold text-slate-900">Request Received!</h4>
                            <p className="text-slate-500 mt-2">We will contact you shortly.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1">Name</label>
                                    <input 
                                        required 
                                        name="name" 
                                        onChange={handleChange} 
                                        value={formData.name} 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" 
                                        placeholder="John Doe" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1">Phone</label>
                                    <input 
                                        required 
                                        name="phone" 
                                        onChange={handleChange} 
                                        value={formData.phone} 
                                        type="tel" 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" 
                                        placeholder="+254..." 
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
                                <input 
                                    required 
                                    name="email" 
                                    onChange={handleChange} 
                                    value={formData.email} 
                                    type="email" 
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" 
                                />
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1">Type</label>
                                    <select 
                                        name="projectType" 
                                        onChange={handleChange} 
                                        value={formData.projectType} 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-500 outline-none"
                                    >
                                        <option>Web Development</option>
                                        <option>Mobile App</option>
                                        <option>System/ERP</option>
                                        <option>Consultancy</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1">Budget</label>
                                    <select 
                                        name="budget" 
                                        onChange={handleChange} 
                                        value={formData.budget} 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-blue-500 outline-none"
                                    >
                                        <option>50k - 100k KES</option>
                                        <option>100k - 300k KES</option>
                                        <option>300k+</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Details</label>
                                <textarea 
                                    required 
                                    name="details" 
                                    onChange={handleChange} 
                                    value={formData.details} 
                                    rows="3" 
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                ></textarea>
                            </div>
                            
                            <button 
                                type="submit" 
                                disabled={status === 'submitting'} 
                                className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                            >
                                {status === 'submitting' ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} /> Sending...
                                    </>
                                ) : (
                                    <>
                                        Submit Request <ArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default QuoteModal;